/** The globe, its state line, and the pocket inside the frame. */
import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useMemoryStore } from "@/stores/memory-store";
import { useProjectStore } from "@/stores/project-store";
import { MemoryGlobe } from "./memory-globe";
import { MemoryPocketHost } from "./memory-pocket-host";
import { MemoryProOnly } from "./memory-pro-only";
import { MemoryStatusLine } from "./memory-status-line";
import { useMemoryFrameKeys } from "./use-memory-frame-keys";

/** Whether the account's plan includes the knowledge graph; "checking" until it is known. */
export type MemoryAccess = "checking" | "open" | "locked";

export function MemoryFrameBody({
  frameId,
  pixelBoost,
  access,
}: {
  frameId: string;
  pixelBoost: number;
  access: MemoryAccess;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const pocketRef = useRef<HTMLDivElement>(null);
  const pocketOpen = useMemoryStore((state) => state.pocketStack.length > 0);
  const projectId = useProjectStore((state) => state.currentProject?.id);
  const { user } = useAuth();
  const youName = user?.name ?? "";
  useMemoryFrameKeys(frameRef, pocketRef);
  useEffect(() => {
    if (access === "open") void useMemoryStore.getState().refresh();
  }, [projectId, access]);

  return (
    <div
      ref={frameRef}
      className="relative min-h-0 flex-1"
      data-frame-content="true"
      data-testid="memory-frame-body"
    >
      {access === "locked" ? (
        <MemoryProOnly />
      ) : (
        <>
          <MemoryGlobe
            frameId={frameId}
            pixelBoost={pixelBoost}
            pocketInside={pocketOpen}
            youName={youName}
            youEmail={user?.email}
          />
          <MemoryStatusLine pocketInside={pocketOpen} />
          <MemoryPocketHost ref={pocketRef} youName={youName} />
        </>
      )}
    </div>
  );
}
